// หน้าแชท (/chat, /chat/:id) — หน้าแรกแบบ Lorka: ทักทาย + ช่องถาม + แนะนำสำหรับคุณ ; มีข้อความแล้วเป็นห้องแชท
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { chats } from "../../services/chats";
import { getSelectedModel, setSelectedModel, isUsable } from "../../services/ai/models";
import { estimateTokens } from "../../services/ai/tokens";
import { fmtNum, fmtTokens } from "../../services/pricing";
import { useAi } from "../../components/chat/useAi";
import Composer from "../../components/chat/Composer";
import ModelPicker from "../../components/chat/ModelPicker";
import ModelLoader from "../../components/chat/ModelLoader";
import Markdown from "../../components/chat/Markdown";
import TopbarSlot from "../../components/layout/TopbarSlot";
import { notifyChats } from "../../components/layout/AppShell";
import { Avatar, Button, Icon, ModelMark } from "../../components/ui";

const SYSTEM = "คุณคือผู้ช่วย AI ของ MixAI ตอบตรงประเด็น สุภาพ เป็นมิตร ใช้ภาษาเดียวกับผู้ใช้ (ผู้ใช้พิมพ์ไทยให้ตอบไทย) จัดรูปแบบด้วย Markdown เมื่อช่วยให้อ่านง่าย ถ้าไม่แน่ใจให้บอกตรงๆ";

const SUGGEST = [
  { icon: "mail", title: "เขียนอีเมล", text: "ช่วยเขียนอีเมลขอเลื่อนประชุมกับลูกค้าแบบสุภาพ" },
  { icon: "file", title: "สรุปเนื้อหา", text: "สรุปประเด็นสำคัญของข้อความต่อไปนี้เป็นข้อๆ:\n" },
  { icon: "bulb", title: "ระดมไอเดีย", text: "คิดไอเดียคอนเทนต์ TikTok สำหรับร้านกาแฟ 10 ไอเดีย" },
  { icon: "code", title: "อธิบายโค้ด", text: "อธิบายว่า JavaScript async/await ทำงานอย่างไร พร้อมตัวอย่าง" },
  { icon: "translate", title: "แปลภาษา", text: "แปลเป็นภาษาอังกฤษแบบธรรมชาติ: " },
  { icon: "brain", title: "วางแผน", text: "ช่วยวางแผนเที่ยวเชียงใหม่ 3 วัน 2 คืน งบ 5,000 บาท" },
];

const RECOMMEND = [
  { to: "/tools/detector", icon: "scan", label: "ตรวจว่าข้อความเขียนโดย AI หรือไม่" },
  { to: "/tools/humanizer", icon: "wand", label: "เกลาข้อความ AI ให้เป็นธรรมชาติ" },
  { to: "/tools/translate", icon: "translate", label: "แปลภาษาแบบมืออาชีพ" },
  { to: "/image", icon: "image", label: "สร้างและแก้ไขภาพ" },
  { to: "/tools", icon: "tools", label: "สำรวจเครื่องมือทั้งหมด" },
];

/** ตัดประวัติให้อยู่ในงบ token (~1,600) เก็บข้อความล่าสุดไว้ก่อน */
const fitHistory = (msgs, budget = 1600) => {
  const out = [];
  let used = 0;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const t = estimateTokens(msgs[i].content);
    if (out.length && used + t > budget) break;
    used += t;
    out.unshift({ role: msgs[i].role, content: msgs[i].content });
  }
  return out;
};

function Message({ m, model, onCopy, onRetry, isLast, streaming }) {
  if (m.role === "user") {
    return (
      <div className="msg msg-user">
        <div className="msg-bubble">{m.content}</div>
      </div>
    );
  }
  return (
    <div className="msg msg-ai">
      <ModelMark model={model} size={30} />
      <div className="msg-body">
        <div className="msg-head">
          <b>{m.modelName || model?.name}</b>
          {m.mock ? <span className="tiny faint">โหมดทดลอง</span> : null}
        </div>
        {m.content ? <Markdown text={m.content} /> : <div className="typing"><span /><span /><span /></div>}
        {streaming ? <span className="caret" /> : null}
        {!streaming && m.content ? (
          <div className="msg-actions">
            <button onClick={() => onCopy(m.content)} title="คัดลอก"><Icon name="copy" size={15} /></button>
            {isLast ? <button onClick={onRetry} title="ตอบใหม่"><Icon name="refresh" size={15} /></button> : null}
            {m.cost ? <span className="msg-cost"><Icon name="zap" size={13} />{fmtNum(m.cost)} token</span> : null}
            {m.stopped ? <span className="msg-cost">หยุดกลางทาง</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Chat() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile, models, balance } = useAuth();
  const [modelId, setModelId] = useState(() => getSelectedModel());
  const model = useMemo(() => models.find((m) => m.id === modelId && isUsable(m)) || models.find(isUsable), [models, modelId]);
  const ai = useAi(model);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamId, setStreamId] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const skipLoad = useRef(null);
  const endRef = useRef(null);
  const listRef = useRef(null);
  const stick = useRef(true);

  // โหลดข้อความเมื่อเปลี่ยนห้อง (ข้ามห้องที่เพิ่งสร้างระหว่างกำลังตอบ)
  useEffect(() => {
    setBlocked(null);
    if (!id) {
      if (!busy) setMessages([]);
      return;
    }
    if (skipLoad.current === id) return;
    let alive = true;
    setLoadingChat(true);
    Promise.all([chats.messages(id), profile?.uid ? chats.get(profile.uid, id) : null])
      .then(([rows, c]) => {
        if (!alive) return;
        setMessages(rows);
        if (c?.model && models.some((m) => m.id === c.model && isUsable(m))) setModelId(c.model);
      })
      .catch(() => alive && setMessages([]))
      .finally(() => alive && setLoadingChat(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.state?.fresh]);

  // มาจากหน้า Landing พร้อมคำถาม
  useEffect(() => {
    if (location.state?.prompt) setInput(location.state.prompt);
  }, [location.state?.prompt]);

  useEffect(() => {
    if (stick.current) endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const pickModel = (mid) => {
    setModelId(mid);
    setSelectedModel(mid);
    if (id && profile?.uid) chats.touch(profile.uid, id, { model: mid }).catch(() => {});
  };

  const send = async (textArg, { retry = false } = {}) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    if (!profile) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    setBlocked(null);
    let chatId = id;
    let base = messages;
    if (retry) {
      base = messages.slice(0, -1); // ทิ้งคำตอบเดิมของข้อความสุดท้าย
    }
    const userMsg = { id: "tmp-u" + Date.now(), role: "user", content: text };
    const aiTmp = { id: "tmp-a" + Date.now(), role: "assistant", content: "", model: model.id, modelName: model.name };
    const nextBase = retry ? base : [...base, userMsg];
    setMessages([...nextBase, aiTmp]);
    setStreamId(aiTmp.id);
    setInput("");
    setBusy(true);
    stick.current = true;
    try {
      if (!chatId) {
        chatId = await chats.create(profile.uid, { title: text.replace(/\s+/g, " ").slice(0, 48), model: model.id });
        skipLoad.current = chatId;
        navigate(`/chat/${chatId}`, { replace: true });
        notifyChats();
      }
      if (!retry) await chats.addMessage(chatId, { role: "user", content: text });
      const r = await ai.run({
        system: SYSTEM,
        messages: fitHistory(nextBase),
        ref: chatId,
        onToken: (_t, full) => setMessages((ms) => ms.map((m) => (m.id === aiTmp.id ? { ...m, content: full } : m))),
      });
      if (!r.ok) {
        setMessages((ms) => ms.filter((m) => m.id !== aiTmp.id));
        if (r.code === "no_tokens" || r.code === "ai_not_ready" || r.code === "ai_unsupported") setBlocked(r);
        else if (r.code !== "ai_cancelled") toast(r.message, "error");
        return;
      }
      const saved = { role: "assistant", content: r.text, model: model.id, modelName: model.name, cost: r.charge?.cost || 0, ...(r.stopped ? { stopped: true } : {}), ...(r.mock ? { mock: true } : {}) };
      const mid = await chats.addMessage(chatId, saved);
      setMessages((ms) => ms.map((m) => (m.id === aiTmp.id ? { id: mid, ...saved } : m)));
      await chats.touch(profile.uid, chatId, { model: model.id });
      notifyChats();
    } catch (e) {
      setMessages((ms) => ms.filter((m) => m.id !== aiTmp.id));
      toast(e.message || "ส่งข้อความไม่สำเร็จ", "error");
    } finally {
      setBusy(false);
      setStreamId(null);
      skipLoad.current = null;
    }
  };

  const copy = (t) => {
    navigator.clipboard?.writeText(t);
    toast("คัดลอกแล้ว", "success");
  };
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const empty = !id && !messages.length;
  const hello = profile?.displayName ? `สวัสดี ${profile.displayName}` : "ยินดีต้อนรับสู่ MixAI";

  const blockedCard = blocked ? (
    blocked.code === "no_tokens" ? (
      <div className="block-card">
        <Icon name="zap" size={22} />
        <div className="min0">
          <b>token หมดแล้ว</b>
          <div className="small muted">อัปเกรดแพ็กเกจเพื่อรับโควตารายเดือน หรือซื้อ token แบบจ่ายตามการใช้งาน</div>
        </div>
        <Link to="/pricing" className="btn btn-primary btn-sm">ดูราคา</Link>
      </div>
    ) : (
      <div className="block-card">
        <Icon name="alert" size={22} />
        <div className="small">{blocked.message}</div>
      </div>
    )
  ) : null;

  return (
    <div className="chat">
      <TopbarSlot>
        <ModelPicker value={model?.id} onChange={pickModel} />
      </TopbarSlot>

      {empty ? (
        <div className="welcome">
          <div className="welcome-hero">
            <div className="welcome-badge"><Icon name="sparkles" size={15} /> รวม AI ชั้นนำไว้ในที่เดียว</div>
            <h1>{hello}</h1>
            <p>วันนี้ให้ MixAI ช่วยอะไรดี?</p>
          </div>
          <div className="welcome-composer">
            <ModelLoader model={model} ai={ai} compact />
            <Composer value={input} onChange={setInput} onSend={() => send()} onStop={ai.abort} busy={busy} big autoFocus placeholder={`ถาม ${model?.name || "AI"} ได้ทุกเรื่อง...`} />
            {blockedCard}
            {profile && balance ? (
              <div className="welcome-meta">
                <Icon name="zap" size={13} /> เหลือ {fmtTokens(balance.total)} token · โมเดลนี้คิด ×{model?.rate} ต่อ token
              </div>
            ) : null}
          </div>
          <div className="suggest-grid">
            {SUGGEST.map((s) => (
              <button key={s.title} className="suggest" onClick={() => setInput(s.text)}>
                <span className="suggest-ic"><Icon name={s.icon} size={18} /></span>
                <span>
                  <b>{s.title}</b>
                  <span className="suggest-text">{s.text.trim()}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="recommend">
            <div className="recommend-title">แนะนำสำหรับคุณ</div>
            <div className="recommend-list">
              {RECOMMEND.map((r) => (
                <Link key={r.to} to={r.to} className="recommend-item">
                  <Icon name={r.icon} size={16} />
                  <span>{r.label}</span>
                  <Icon name="right" size={15} className="faint" />
                </Link>
              ))}
            </div>
          </div>
          {(!profile || balance?.planId === "free") ? (
            <Link to="/pricing" className="promo">
              <div>
                <div className="promo-kicker"><Icon name="crown" size={14} /> MixAI Pro</div>
                <b>ใช้โมเดลมากขึ้น ได้ token มากขึ้น ในราคาเดียว</b>
                <span className="small">เริ่มต้น ฿300/เดือน · ปรับโควตาเองได้ หรือจ่ายตามการใช้งาน</span>
              </div>
              <span className="btn btn-light btn-sm">ดูแพ็กเกจ</span>
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          <div
            className="chat-scroll"
            ref={listRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
            }}
          >
            <div className="chat-list">
              {loadingChat && !messages.length ? <div className="center faint mt-24">กำลังโหลดแชท...</div> : null}
              {messages.map((m, i) => (
                <Message
                  key={m.id}
                  m={m}
                  model={models.find((x) => x.id === m.model) || model}
                  onCopy={copy}
                  onRetry={() => lastUser && send(lastUser.content, { retry: true })}
                  isLast={i === messages.length - 1}
                  streaming={streamId === m.id && !!m.content}
                />
              ))}
              <div ref={endRef} />
            </div>
          </div>
          <div className="chat-bottom">
            <ModelLoader model={model} ai={ai} compact />
            {blockedCard}
            <Composer value={input} onChange={setInput} onSend={() => send()} onStop={ai.abort} busy={busy} autoFocus placeholder={`ส่งข้อความถึง ${model?.name || "AI"}...`} />
            <div className="chat-disclaimer">AI อาจให้ข้อมูลผิดพลาดได้ ควรตรวจสอบข้อมูลสำคัญอีกครั้ง</div>
          </div>
        </>
      )}
      {!profile && empty ? (
        <div className="guest-note">
          <Avatar name="M" size={22} />
          <span>สมัครฟรีวันนี้ รับ 40,000 token ใช้งานทันที</span>
          <Link to="/register"><Button size="sm" variant="primary">สมัครฟรี</Button></Link>
        </div>
      ) : null}
    </div>
  );
}
